import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { useAsyncFn } from 'react-use';
import cn from 'classnames';

import MoneyInput from 'components/form/MoneyInput.component';
import CurrencySign from 'components/price/CurrencySign.component';
import SYMBOLS from 'constants/form/symbols.const';
import S from 'constants/string.const';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectCheckoutDataVouchersUsed } from 'selectors/checkout.selectors';
import asArray from 'utils/arrays/asArray.util';
import { fromEventToValue } from 'utils/converters/event.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import call from 'utils/functions/call.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';

import CN from './Voucher.module.scss';

const L = logger('Voucher');

const MONEY_INPUT_PLACEHOLDER = '0.00';

const Voucher = ({ checkoutVouchersUsed, index, onVoucherAmountChange, onFound, total, warning, vouchers: vouchersFromProps }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const vouchers = asArray(vouchersFromProps);
  const hasVoucher = fromAnyToBool(vouchers[index]);
  const { amount, state } = asObject(vouchers[index]);

  const [voucherNumber, setVoucherNumber] = useStateWhileMounted('');
  const [voucherError, setVoucherError] = useStateWhileMounted('');

  const usedAmount = fromAnyToString(checkoutVouchersUsed?.[index]?.used_amount);
  const amountWarning = total >= 0 ? '' : t('checkout.giftVouchers.amountWarningMsg');

  const isIndexWithinLimit = fromAnyToBool(vouchers.length > index);
  const isDuplicate = vouchers.map((v) => v?.number).indexOf(voucherNumber) >= 0;

  const handleChange = useCallback(
    //
    (ev) => void call('Voucher.onVoucherAmountChange', onVoucherAmountChange, { ...ev, index }),
    [index, onVoucherAmountChange],
  );

  const handleCode = useCallback(
    (ev) => {
      const value = fromEventToValue(ev);

      if (value.includes(SYMBOLS.amp)) {
        setVoucherError(t('checkout.giftVouchers.voucherSymbolErrorMsg'));
      } else {
        setVoucherError(S.empty);
      }

      setVoucherNumber(value);
    },
    [setVoucherNumber, setVoucherError, t],
  );

  const [{ loading: isLoading }, handleFind] = useAsyncFn(async () => {
    if (voucherNumber.includes(SYMBOLS.amp)) {
      setVoucherError(t('checkout.giftVouchers.voucherErrorMsg'));
      return;
    }

    if (isDuplicate) {
      L.debug('handleFind()', { isDuplicate });
      setVoucherError(t('checkout.giftVouchers.duplicateErrorMsg'));
      return;
    }

    const result = await call('Voucher.onFound', onFound, { index, number: voucherNumber });

    L.debug('handleFind()', result);

    if (false === result?.success) {
      setVoucherError(t('checkout.giftVouchers.voucherErrorMsg'));
      setVoucherNumber(S.empty);
    }
    //
  }, [index, isDuplicate, onFound, setVoucherError, setVoucherNumber, t, voucherNumber]);

  const hasViableVoucher = fromAnyToBool(!isLoading && vouchersFromProps && hasVoucher && !voucherError);

  return (
    <div
      data-bem="Voucher"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div className={CN.bigBox}>
        <span className={CN.label}>{t('checkout.giftVouchers.inputLabel')}</span>
        <input
          data-bem="Voucher__code-input"
          type="text"
          className={CN.input}
          autoCorrect="off"
          autoCapitalize="none"
          placeholder={t('checkout.giftVouchers.inputPlaceholder')}
          value={voucherNumber}
          onChange={handleCode}
        />

        {fromAnyToBool(voucherNumber) && (
          <button data-bem="Voucher__find-button" data-cy="checkout-product-button" className="btn-find-voucher" onClick={handleFind}>
            {t('checkout.giftVouchers.findVoucherButton')}
          </button>
        )}

        <div data-bem="Voucher__state" className={CN.warning}>
          {hasViableVoucher && (
            <span
              className={cn({
                [`btn-code-${state}`]: state,
              })}
            >
              {state}
            </span>
          )}
        </div>
      </div>

      {hasVoucher && (
        <>
          <div data-bem="Voucher__mid-box" className={CN.midBox}>
            <span className={CN.label}>{t('checkout.giftVouchers.balance')}</span>
            {isIndexWithinLimit && (
              <span className={CN.value}>
                <CurrencySign value={amount} />
              </span>
            )}
          </div>
          <div data-bem="Voucher__money-box" className={CN.moneyBox}>
            <div className={CN.moneyInput}>
              <span className={CN.label}>{t('checkout.giftVouchers.use')}</span>
              <div className={CN.value}>
                <MoneyInput onChange={handleChange} value={usedAmount} placeholder={MONEY_INPUT_PLACEHOLDER} displayDecimals="mixed" />
              </div>
            </div>

            <div className={CN.warning}>
              <span>{amountWarning}</span> <span>{warning}</span>
            </div>
          </div>
          <div data-bem="Voucher__empty-box" className={CN.emptyBox} />
        </>
      )}
      <div data-bem="Voucher__error-message" className={CN.errorNotFound}>
        {fromAnyToBool(voucherError) && <div className={CN.errorCode}>{voucherError}</div>}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  checkoutVouchersUsed: selectCheckoutDataVouchersUsed(state),
});

const VoucherHoc = connect(mapStateToProps)(Voucher);

export default VoucherHoc;
